// Asking the server for prices without hanging, flooding or repeating itself.
//  - one request at a time (the class tiles are asked in order, each shows as soon as it arrives)
//  - every request has a time limit and is retried once when it fails in a way that may pass (busy, rate limit, network)
//  - an answer for the same question is reused for a few minutes and a question already on its way is shared, so the total for the chosen class
//    costs no extra request when its tile was just priced (the server allows 30 price requests a minute per account)
export const UNAVAILABLE = 'Price unavailable — try again';

const RETRYABLE = new Set([0, 408, 425, 429, 500, 502, 503, 504]);

export function createQuoteClient({ api, getToken = () => null, fetchImpl, timeoutMs = 20000, retries = 1, retryDelayMs = 1500, cacheMs = 300000, now = () => Date.now(),
  sleep = (ms) => new Promise((r) => setTimeout(r, ms)) } = {}) {
  const f = (...a) => (fetchImpl || fetch)(...a);
  const cache = new Map();           // key -> { at, result }
  const inflight = new Map();        // key -> Promise

  async function once(body, signal) {
    const ctl = typeof AbortController !== 'undefined' ? new AbortController() : null;
    const timer = ctl ? setTimeout(() => ctl.abort(), timeoutMs) : null;
    const onAbort = () => ctl && ctl.abort();
    if (signal && signal.addEventListener) signal.addEventListener('abort', onAbort);
    try {
      const t = getToken();
      const r = await f(`${api}/api/quote`, { method: 'POST', headers: { 'Content-Type': 'application/json', ...(t ? { Authorization: `Bearer ${t}` } : {}) }, body: JSON.stringify(body), ...(ctl ? { signal: ctl.signal } : {}) });
      const j = await r.json().catch(() => ({}));
      if (r.ok) return { ok: true, quote: j };
      const d = j && j.detail;
      const text = typeof d === 'string' ? d : (d && d.message) || '';
      const retryAfter = Number(r.headers && r.headers.get && r.headers.get('Retry-After')) || 0;
      return { ok: false, status: r.status, retryable: RETRYABLE.has(r.status), retryAfterMs: Math.min(5000, retryAfter * 1000), message: text || (r.status === 429 ? 'Too many price requests. Please wait a moment.' : UNAVAILABLE) };
    } catch (e) {
      if (signal && signal.aborted) return { ok: false, status: -1, cancelled: true, retryable: false, message: '' };
      return { ok: false, status: 0, retryable: true, message: UNAVAILABLE };        // no answer in time, or no connection
    } finally {
      if (timer) clearTimeout(timer);
      if (signal && signal.removeEventListener) signal.removeEventListener('abort', onAbort);
    }
  }

  async function ask(body, signal) {
    let res = await once(body, signal);
    for (let i = 0; i < retries && !res.ok && res.retryable && !(signal && signal.aborted); i += 1) {
      await sleep(res.retryAfterMs || retryDelayMs);
      res = await once(body, signal);
    }
    return res;
  }

  function quote(body, signal) {
    const key = JSON.stringify([body, !!getToken()]);
    const hit = cache.get(key);
    if (hit && now() - hit.at < cacheMs) return Promise.resolve(hit.result);
    if (inflight.has(key)) return inflight.get(key);
    const p = ask(body, signal).then((result) => {
      if (result.ok) cache.set(key, { at: now(), result });
      return result;
    }).finally(() => inflight.delete(key));
    inflight.set(key, p);
    return p;
  }

  async function quoteEach(items, onEach, signal) {              // [{ id, body }] one after the other
    for (const it of items) {
      if (signal && signal.aborted) return;
      const res = await quote(it.body, signal);
      if (signal && signal.aborted) return;
      onEach(it.id, res);
    }
  }

  return { quote, quoteEach, clear: () => cache.clear() };
}
